import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Radio,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  HourglassOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import lib from '../../../assets/utils/lib';
import { useWebSocket } from '../../../assets/utils/useWebSocket';
import OrganizationAutocomplete from './OrganizationAutocomplete';
import OrgBulkUploadLogsPanel from './OrgBulkUploadLogsPanel';
import { orgDetailPathFromRequest } from '../planRequestHelpers';
import { formatPlanEnrollmentRowError } from '../../plans/planCatalogHelpers';
import EnrollmentPausedBanner from './EnrollmentPausedBanner';
import {
  enrollmentApplyResultAction,
  enrollmentPausedNotification,
  isEnrollmentNeedsResolution,
  resolvePlainOrgCode,
} from '../enrollmentConflictHelpers';

const { Text } = Typography;

const STATUS_COLORS = {
  pending: 'processing',
  marked_approve: 'cyan',
  marked_reject: 'volcano',
  partially_reviewed: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'default',
  failed: 'error',
  applied: 'success',
};

/** Row decision labels shown to reviewers (maps API mark_* statuses). */
const ITEM_DECISION_LABELS = {
  pending: 'Pending',
  marked_approve: 'Approve',
  marked_reject: 'Reject',
  applied: 'Approved',
  approved: 'Approved',
  rejected: 'Rejected',
  failed: 'Failed',
};

const itemDecisionValue = (status) => {
  if (status === 'marked_approve') return 'approve';
  if (status === 'marked_reject') return 'reject';
  return 'pending';
};

const ACTION_COLORS = {
  create_new: 'green',
  update_existing: 'orange',
  reactivate: 'blue',
  deactivate: 'red',
  activate: 'geekblue',
};

const SOURCE_LABELS = {
  bulk_csv: 'Bulk upload',
  staff_add: 'Staff add',
  staff_deactivate: 'Staff deactivate',
  staff_activate: 'Staff activate',
  single_request: 'Single request',
};

const formatLabel = (value) => String(value || '—').replaceAll('_', ' ');
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatSource = (row) => row?.source_label || SOURCE_LABELS[row?.source] || formatLabel(row?.source);

const normalizeList = (parsed) => {
  const payload = parsed?.data || {};
  return {
    rows: Array.isArray(payload) ? payload : (payload.rows || []),
    meta: parsed?.meta || payload.meta || {},
  };
};

const normalizeBatch = (parsed) => {
  const payload = parsed?.data || {};
  const batch = payload.batch || payload;
  return {
    ...batch,
    items: payload.items || batch.items || [],
  };
};

const isActionableBatchStatus = (status) => (
  ['pending', 'partially_reviewed', 'failed'].includes(String(status || '').toLowerCase())
);

const canDecideItem = (item) => (
  ['pending', 'marked_approve', 'marked_reject'].includes(String(item?.status || '').toLowerCase())
);

const awaitingReviewCount = (row) => (
  (Number(row?.pending_count) || 0)
  + (Number(row?.marked_approve_count) || 0)
  + (Number(row?.marked_reject_count) || 0)
);

// Per-item result/error text can arrive under several keys depending on backend build.
const ITEM_MESSAGE_KEYS = [
  'result_message', 'error_message', 'message', 'error',
  'result', 'apply_message', 'failure_reason', 'reason', 'detail', 'note',
];

const getItemMessage = (item) => {
  if (!item) return '';
  const applyAction = enrollmentApplyResultAction(item);
  if (applyAction) return applyAction;
  for (const key of ITEM_MESSAGE_KEYS) {
    const val = item[key];
    if (val != null && String(val).trim() !== '') {
      const code = item.code || item.error_code || item.result_code;
      const raw = code && !String(val).includes(String(code))
        ? `${String(val).trim()} [${code}]`
        : String(val).trim();
      return formatPlanEnrollmentRowError(raw).display;
    }
  }
  if (item.code) {
    return formatPlanEnrollmentRowError(`[${item.code}]`).display;
  }
  return '';
};

/** Pull per-row failure reasons out of an apply-failure response body, if present. */
const extractFailureRows = (parsed) => {
  const data = parsed?.data ?? parsed?.body?.data ?? {};
  const lists = [data.items, data.failed_items, data.errors, data.row_errors].filter(Array.isArray);
  const rows = [];
  lists.forEach((list) => list.forEach((entry) => {
    const msg = getItemMessage(entry);
    if (!msg) return;
    const row = entry?.row_number ?? entry?.row ?? null;
    const name = entry?.name || '';
    const prefix = [row != null ? `Row ${row}` : null, name].filter(Boolean).join(' · ');
    rows.push(prefix ? `${prefix}: ${msg}` : msg);
  }));
  return rows;
};

const StaffUploadsPanel = ({
  filterOrgId = null,
  filterOrgLabel = '',
  filterSocialNo = null,
  onOrgFilterChange,
  showOrgFilter = true,
  onPendingCountChange,
  showApprovalQueue = true,
  showUploadHistory = true,
}) => {
  const navigate = useNavigate();
  const [panelMode, setPanelMode] = useState(showApprovalQueue ? 'queue' : 'history');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20 });
  const [status, setStatus] = useState('all');
  const [sourceGroup, setSourceGroup] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(
    filterOrgId
      ? { org_id: filterOrgId, org_name: filterOrgLabel, org_social_no: filterSocialNo }
      : null,
  );
  const [batch, setBatch] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [openingBatchId, setOpeningBatchId] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [actionType, setActionType] = useState('all');
  const [itemStatus, setItemStatus] = useState('all');
  const [reviewingKey, setReviewingKey] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState([]);

  useEffect(() => {
    setSelectedOrg(
      filterOrgId
        ? { org_id: filterOrgId, org_name: filterOrgLabel, org_social_no: filterSocialNo }
        : null,
    );
  }, [filterOrgId, filterOrgLabel, filterSocialNo]);

  useEffect(() => {
    if (panelMode === 'queue' && !showApprovalQueue && showUploadHistory) {
      setPanelMode('history');
    }
    if (panelMode === 'history' && !showUploadHistory && showApprovalQueue) {
      setPanelMode('queue');
    }
  }, [panelMode, showApprovalQueue, showUploadHistory]);

  const historySocialNo = resolvePlainOrgCode(
    filterSocialNo,
    selectedOrg?.org_social_no,
    selectedOrg?.social_no,
  ) || null;

  const loadBatches = useCallback(async (page = 1, limit = meta.limit) => {
    setLoading(true);
    setError('');
    const parsed = lib.parseApiEnvelope(await lib.getOrganizationStaffUploads({
      status: status === 'all' ? 'all' : status,
      org_id: selectedOrg?.org_id || undefined,
      source_group: sourceGroup === 'all' ? 'all' : sourceGroup,
      page,
      limit,
    }));
    if (parsed.ok) {
      const result = normalizeList(parsed);
      setRows(result.rows);
      setMeta({
        total: Number(result.meta.total) || 0,
        page: Number(result.meta.page) || page,
        limit: Number(result.meta.limit) || limit,
      });
      if (status === 'pending') onPendingCountChange?.(Number(result.meta.total) || 0);
    } else {
      setRows([]);
      setError(parsed.message || 'Unable to load staff upload batches.');
    }
    setLoading(false);
  }, [meta.limit, onPendingCountChange, selectedOrg?.org_id, sourceGroup, status]);

  useEffect(() => {
    if (panelMode !== 'queue' || !showApprovalQueue) return undefined;
    loadBatches(1, meta.limit);
    return undefined;
  }, [status, selectedOrg?.org_id, sourceGroup, panelMode, showApprovalQueue]); // eslint-disable-line react-hooks/exhaustive-deps

  const orgDetailPath = (row) => orgDetailPathFromRequest(row);

  const loadBatch = useCallback(async (batchId, silent = false) => {
    if (!silent) {
      setDetailLoading(true);
      setOpeningBatchId(batchId);
    }
    const parsed = lib.parseApiEnvelope(await lib.getOrganizationStaffUpload(batchId));
    if (parsed.ok) {
      const nextBatch = normalizeBatch(parsed);
      setBatch(nextBatch);
      if (!silent) {
        setSelectedItemIds([]);
        const nextItems = nextBatch.items || [];
        const pendingCount = Number(nextBatch.pending_count)
          || nextItems.filter((item) => item.status === 'pending').length;
        setActionType('all');
        setItemStatus(
          isActionableBatchStatus(nextBatch.status) && pendingCount > 0 ? 'pending' : 'all',
        );
      }
    } else {
      notification.error({ message: parsed.message || 'Unable to load batch details.' });
    }
    setDetailLoading(false);
    setOpeningBatchId(null);
  }, []);

  const batchIdRef = useRef(null);
  const metaRef = useRef(meta);
  batchIdRef.current = batch?.id || null;
  metaRef.current = meta;

  const handleStaffUploadWs = useCallback((message) => {
    const eventType = message?.event || message?.type;
    if (eventType !== 'staff_upload_updated') return;

    const payload = message?.payload || message || {};
    const action = String(payload.action || '').toLowerCase();
    const orgName = payload.org_name || payload.organization_name || 'Organization';

    if (action === 'submitted') {
      notification.info({
        message: 'New staff upload',
        description: `${orgName} submitted a staff upload for review.`,
        duration: 4,
      });
    }

    loadBatches(metaRef.current.page, metaRef.current.limit);
    if (batchIdRef.current) {
      const eventBatchId = payload.batch_id || payload.id;
      if (!eventBatchId || Number(eventBatchId) === Number(batchIdRef.current)) {
        loadBatch(batchIdRef.current, true);
      }
    }
  }, [loadBatch, loadBatches]);

  useWebSocket('corporations', {
    enabled: panelMode === 'queue' && showApprovalQueue,
    onMessage: handleStaffUploadWs,
    autoReconnect: true,
  });

  const reviewErrorDescription = (parsed) => {
    const code = parsed?.code || parsed?.body?.code || parsed?.data?.code;
    if (code === 'STAFF_UPLOAD_COLLATION_MISMATCH') {
      return 'Database charset conflict on update/reactivate. Ask ops to run the utf8mb4 collation migration.';
    }
    return undefined;
  };

  const runDecision = useCallback(async (target, decision, item = null) => {
    if (!batch) return false;
    const key = `${target}-${decision}-${item?.id || 'batch'}`;
    setReviewingKey(key);

    let response;
    if (target === 'batch' && decision === 'apply-decisions') {
      response = await lib.applyOrganizationStaffUploadDecisions(batch.id, {});
    } else if (target === 'batch') {
      response = await lib.reviewOrganizationStaffUpload(batch.id, decision, {});
    } else {
      response = await lib.reviewOrganizationStaffUploadItem(item.id, decision, {});
    }

    const parsed = lib.parseApiEnvelope(response);
    if (parsed.ok) {
      const pauseData = parsed.data || {};
      if (
        (target === 'batch' && decision === 'approve' && isEnrollmentNeedsResolution(pauseData))
        || pauseData?.validation_summary?.enrollment_status === 'needs_resolution'
      ) {
        notification.info({
          ...enrollmentPausedNotification(pauseData?.validation_summary || pauseData),
          duration: 8,
        });
        await Promise.all([loadBatches(meta.page, meta.limit), loadBatch(batch.id, true)]);
        setReviewingKey('');
        return true;
      }
      const successMessages = {
        approve: 'Approved',
        reject: 'Rejected',
        'mark-approve': 'Set to approve',
        'mark-reject': 'Set to reject',
        'apply-decisions': 'Decisions applied',
      };
      notification.success({ message: successMessages[decision] || 'Saved' });
      setSelectedItemIds([]);
      await Promise.all([loadBatches(meta.page, meta.limit), loadBatch(batch.id, true)]);
      setReviewingKey('');
      return true;
    }

    const code = parsed?.code || parsed?.body?.code || parsed?.data?.code;
    const isApplyFailure = code === 'STAFF_UPLOAD_APPLY_FAILED'
      || code === 'STAFF_UPLOAD_COLLATION_MISMATCH';

    // Reload so failed row statuses / messages are reflected, then surface them.
    await Promise.all([loadBatches(meta.page, meta.limit), loadBatch(batch.id, true)]);
    if (isApplyFailure) {
      setActionType('all');
      setItemStatus('failed');
    }

    const rowReasons = extractFailureRows(parsed);
    const hint = reviewErrorDescription(parsed);
    const parts = [];
    if (parsed.message) parts.push(parsed.message);
    if (hint) parts.push(hint);
    if (rowReasons.length) {
      parts.push(rowReasons.slice(0, 5).join('\n')
        + (rowReasons.length > 5 ? `\n…and ${rowReasons.length - 5} more` : ''));
    }
    if (isApplyFailure) {
      parts.push('No changes were saved (rolled back). See the highlighted failed rows or download the report for full per-row reasons.');
    }

    notification.error({
      message: isApplyFailure ? 'Upload could not be applied' : (parsed.message || 'Action failed'),
      description: parts.length
        ? <div style={{ whiteSpace: 'pre-line' }}>{parts.filter(Boolean).join('\n')}</div>
        : undefined,
      duration: isApplyFailure ? 10 : 6,
    });
    setReviewingKey('');
    return false;
  }, [batch, loadBatch, loadBatches, meta.limit, meta.page]);

  const runBulkMark = useCallback(async (decision) => {
    if (!batch || !selectedItemIds.length) return;
    const items = (batch.items || []).filter(
      (item) => selectedItemIds.includes(item.id) && canDecideItem(item),
    );
    if (!items.length) {
      notification.info({ message: 'No selectable rows in the current selection' });
      return;
    }

    const key = `bulk-${decision}`;
    setReviewingKey(key);
    let ok = 0;
    let failed = 0;
    let lastError = '';

    for (const item of items) {
      const response = await lib.reviewOrganizationStaffUploadItem(item.id, decision, {});
      const parsed = lib.parseApiEnvelope(response);
      if (parsed.ok) ok += 1;
      else {
        failed += 1;
        lastError = parsed.message || lastError;
      }
    }

    await Promise.all([loadBatches(meta.page, meta.limit), loadBatch(batch.id, true)]);
    setSelectedItemIds([]);
    setReviewingKey('');

    if (failed === 0) {
      notification.success({
        message: decision === 'mark-approve'
          ? `Marked ${ok} row${ok === 1 ? '' : 's'} to approve`
          : `Marked ${ok} row${ok === 1 ? '' : 's'} to reject`,
      });
    } else {
      notification.warning({
        message: `${ok} marked, ${failed} failed`,
        description: lastError || undefined,
      });
    }
  }, [batch, loadBatch, loadBatches, meta.limit, meta.page, selectedItemIds]);

  const downloadReport = async () => {
    if (!batch?.id) return;
    setDownloadingReport(true);
    try {
      await lib.downloadOrganizationStaffUploadReport(
        batch.id,
        batch.filename ? `${batch.filename.replace(/\.[^.]+$/, '')}_report.csv` : undefined,
      );
      notification.success({ message: 'Report downloaded' });
    } catch (err) {
      notification.error({ message: err?.message || 'Unable to download report.' });
    } finally {
      setDownloadingReport(false);
    }
  };

  const queueColumns = [
    {
      title: 'Organization',
      key: 'organization',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.org_name || '—'}</Text>
          <Text type="secondary">{row.org_code || row.social_no || '—'}</Text>
        </Space>
      ),
    },
    {
      title: 'Upload',
      key: 'upload',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Space size={6} wrap>
            <Tag>{formatSource(row)}</Tag>
            <Text strong>{row.filename || `Batch #${row.id}`}</Text>
          </Space>
          <Text type="secondary">{Number(row.item_count) || 0} items</Text>
        </Space>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'submitted_at',
      render: formatDate,
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, row) => {
        const awaiting = awaitingReviewCount(row);
        return (
          <Space direction="vertical" size={4}>
            <Tag color={STATUS_COLORS[row.status]}>{formatLabel(row.status)}</Tag>
            {awaiting > 0 ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {awaiting} awaiting decision
              </Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: '',
      key: 'action',
      fixed: 'right',
      width: 96,
      render: (_, row) => {
        const path = orgDetailPath(row);
        if (!path || !showOrgFilter) return null;
        return (
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              navigate(path);
            }}
          >
            View org
          </Button>
        );
      },
    },
  ];

  const batchItems = useMemo(() => batch?.items || [], [batch?.items]);
  const batchHasMessages = useMemo(
    () => batchItems.some((item) => getItemMessage(item) || enrollmentApplyResultAction(item)),
    [batchItems],
  );

  const filteredItems = useMemo(() => batchItems.filter((item) => (
    (actionType === 'all' || item.action_type === actionType)
    && (itemStatus === 'all' || item.status === itemStatus)
  )), [actionType, batchItems, itemStatus]);

  const itemRowClassName = (item) => {
    if (item.action_type === 'reactivate') return 'staff-upload-item--reactivate';
    if (item.action_type === 'update_existing') return 'staff-upload-item--update';
    if (item.action_type === 'deactivate') return 'staff-upload-item--deactivate';
    return '';
  };

  const pendingItems = Number(batch?.pending_count)
    || batchItems.filter((item) => canDecideItem(item)).length;

  const enrollmentSummary = batch?.validation_summary || {};
  const enrollmentNeedsResolution = String(enrollmentSummary.enrollment_status || '').toLowerCase() === 'needs_resolution'
    || isEnrollmentNeedsResolution(enrollmentSummary)
    || isEnrollmentNeedsResolution(batch);
  const enrollmentSessionId = enrollmentSummary.enrollment_session_id
    || batch?.enrollment_session_id
    || null;

  const markedApproveCount = Number(batch?.marked_approve_count)
    || batchItems.filter((item) => item.status === 'marked_approve').length;

  const markedRejectCount = Number(batch?.marked_reject_count)
    || batchItems.filter((item) => item.status === 'marked_reject').length;

  const truePendingCount = batchItems.filter((item) => item.status === 'pending').length;
  const hasMarkedDecisions = markedApproveCount + markedRejectCount > 0;
  const batchIsActionable = isActionableBatchStatus(batch?.status);
  const busy = Boolean(reviewingKey);
  const selectedDecidableCount = useMemo(
    () => batchItems.filter((item) => selectedItemIds.includes(item.id) && canDecideItem(item)).length,
    [batchItems, selectedItemIds],
  );

  const itemRowSelection = useMemo(() => {
    if (!batchIsActionable) return undefined;
    return {
      columnWidth: 48,
      selectedRowKeys: selectedItemIds,
      onChange: (keys) => setSelectedItemIds(keys),
      getCheckboxProps: (item) => ({
        disabled: busy || !canDecideItem(item),
        name: item.name || `row-${item.id}`,
      }),
    };
  }, [batchIsActionable, busy, selectedItemIds]);

  const decisionMetrics = useMemo(() => {
    const metrics = [];
    if (batchIsActionable) {
      metrics.push(
        { key: 'pending', label: 'Pending', value: truePendingCount, tone: 'pending' },
        ...(markedApproveCount
          ? [{ key: 'approve', label: 'Approve', value: markedApproveCount, tone: 'mark-approve' }]
          : []),
        ...(markedRejectCount
          ? [{ key: 'reject', label: 'Reject', value: markedRejectCount, tone: 'mark-reject' }]
          : []),
      );
    }
    metrics.push(
      {
        key: 'applied',
        label: 'Approved',
        value: Number(batch?.applied_count ?? batch?.approved_count) || 0,
        tone: 'applied',
      },
      {
        key: 'rejected',
        label: 'Rejected',
        value: Number(batch?.rejected_count) || 0,
        tone: 'rejected',
      },
    );
    const failedCount = Number(batch?.failed_count) || 0;
    if (failedCount > 0) {
      metrics.push({ key: 'failed', label: 'Failed', value: failedCount, tone: 'failed' });
    }
    return metrics;
  }, [batch, batchIsActionable, markedApproveCount, markedRejectCount, truePendingCount]);

  const itemColumns = useMemo(() => {
    const columns = [
      { title: 'Row', dataIndex: 'row_number', width: 64 },
      {
        title: 'Staff',
        key: 'staff',
        render: (_, item) => (
          <Space direction="vertical" size={0}>
            <Text strong>{item.name || '—'}</Text>
            <Text type="secondary">{item.phone || item.email || '—'}</Text>
          </Space>
        ),
      },
      {
        title: 'Intent',
        dataIndex: 'action_type',
        width: 120,
        render: (value) => <Tag color={ACTION_COLORS[value]}>{formatLabel(value)}</Tag>,
      },
      {
        title: 'Plan',
        key: 'plan',
        render: (_, item) => (
          <Space direction="vertical" size={0}>
            <Text>{item.insurance_package || item.plans_id || '—'}</Text>
            <Text type="secondary">{formatLabel(item.premium_type)}</Text>
          </Space>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 110,
        render: (value, item) => {
          const message = getItemMessage(item);
          const tag = (
            <Tag color={STATUS_COLORS[value]}>
              {ITEM_DECISION_LABELS[value] || formatLabel(value)}
            </Tag>
          );
          return message ? <Tooltip title={message}>{tag}</Tooltip> : tag;
        },
      },
    ];

    if (batchHasMessages) {
      columns.push({
        title: 'Result',
        key: 'result',
        render: (_, item) => {
          const message = getItemMessage(item);
          if (!message) return <Text type="secondary">—</Text>;
          const formatted = formatPlanEnrollmentRowError(message);
          const isFailed = String(item.status || '').toLowerCase() === 'failed';
          return (
            <Tooltip title={formatted.display}>
              <Space size={4} wrap>
                <Text type={isFailed ? 'danger' : 'secondary'} ellipsis style={{ maxWidth: 220, display: 'inline-block' }}>
                  {formatted.text}
                </Text>
                {formatted.code ? <Tag color="error">{formatted.code}</Tag> : null}
              </Space>
            </Tooltip>
          );
        },
      });
    }

    if (batchIsActionable) {
      columns.push({
        title: 'Decision',
        key: 'decision',
        fixed: 'right',
        width: 128,
        align: 'center',
        render: (_, item) => {
          if (!canDecideItem(item)) return null;
          const value = itemDecisionValue(item.status);
          return (
            <Radio.Group
              className="staff-upload-decision-group"
              size="small"
              optionType="button"
              buttonStyle="solid"
              disabled={busy}
              value={value}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                event.stopPropagation();
                const next = event.target.value;
                if (next === value) return;
                if (next === 'approve') runDecision('item', 'mark-approve', item);
                else if (next === 'reject') runDecision('item', 'mark-reject', item);
              }}
              options={[
                {
                  label: (
                    <Tooltip title="Approve">
                      <CheckOutlined aria-hidden />
                    </Tooltip>
                  ),
                  value: 'approve',
                },
                {
                  label: (
                    <Tooltip title="Pending">
                      <HourglassOutlined aria-hidden />
                    </Tooltip>
                  ),
                  value: 'pending',
                  disabled: value !== 'pending',
                },
                {
                  label: (
                    <Tooltip title="Reject">
                      <CloseOutlined aria-hidden />
                    </Tooltip>
                  ),
                  value: 'reject',
                },
              ]}
              aria-label={`Decision for ${item.name || 'row'}`}
            />
          );
        },
      });
    }

    return columns;
  }, [batchIsActionable, batchHasMessages, busy, runDecision]);

  const modeOptions = [
    ...(showApprovalQueue ? [{ label: 'Needs review', value: 'queue' }] : []),
    ...(showUploadHistory ? [{ label: 'Past uploads', value: 'history' }] : []),
  ];

  return (
    <>
      {modeOptions.length > 1 ? (
        <div className="staff-upload-panel__modes">
          <Segmented
            value={panelMode}
            onChange={setPanelMode}
            options={modeOptions}
          />
          <Text type="secondary" className="staff-upload-panel__purpose">
            {panelMode === 'queue'
              ? 'HR submissions that must be approved before staff records are written. The account team is emailed when HR submits a new batch.'
              : 'Read-only audit trail for uploads that were applied immediately.'}
          </Text>
        </div>
      ) : null}

      {panelMode === 'history' && showUploadHistory ? (
        historySocialNo ? (
          <OrgBulkUploadLogsPanel
            socialNo={historySocialNo}
            orgName={selectedOrg?.org_name || filterOrgLabel || ''}
          />
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {showOrgFilter ? (
              <div className="org-approval-org-filter">
                <OrganizationAutocomplete
                  key={selectedOrg?.org_id || 'history-org'}
                  placeholder={selectedOrg?.org_name || 'Select an organization to view upload history'}
                  onSelect={(org) => {
                    setSelectedOrg(org);
                    onOrgFilterChange?.(org);
                  }}
                />
              </div>
            ) : null}
            <Empty description="Select an organization to view legacy upload history." />
            <Alert
              type="info"
              showIcon
              message="Past uploads are already applied."
              description="New HR submissions appear under Needs review and must be approved before staff records are written. The account team is emailed when HR submits."
            />
          </Space>
        )
      ) : (
        <>
          <div className="staff-upload-panel__source-tabs">
            <Segmented
              value={sourceGroup}
              onChange={setSourceGroup}
              options={[
                { label: 'All requests', value: 'all' },
                { label: 'Bulk uploads', value: 'bulk' },
                { label: 'Single staff requests', value: 'staff_requests' },
              ]}
            />
            <Text type="secondary" className="staff-upload-panel__purpose">
              {sourceGroup === 'bulk'
                ? 'CSV batches uploaded by HR.'
                : sourceGroup === 'staff_requests'
                  ? 'Individual staff add / activation / removal requests.'
                  : 'All staff submissions awaiting review.'}
            </Text>
          </div>
          <div className="org-approval-toolbar">
            <Space wrap>
              <Select
                value={status}
                onChange={setStatus}
                options={[
                  { value: 'all', label: 'All statuses' },
                  ...[
                    'pending',
                    'partially_reviewed',
                    'approved',
                    'rejected',
                    'cancelled',
                    'failed',
                  ].map((value) => ({ value, label: formatLabel(value) })),
                ]}
                style={{ minWidth: 170 }}
              />
              {showOrgFilter ? (
                <div className="org-approval-org-filter">
                  <OrganizationAutocomplete
                    key={selectedOrg?.org_id || 'all'}
                    placeholder={selectedOrg?.org_name || 'Filter by organization'}
                    onSelect={(org) => {
                      setSelectedOrg(org);
                      onOrgFilterChange?.(org);
                    }}
                  />
                </div>
              ) : null}
            </Space>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadBatches(meta.page, meta.limit)}>
              Refresh
            </Button>
          </div>

          {error ? <Alert type="error" showIcon message={error} className="orgs-error-alert" /> : null}
          <Table
            className="org-policies-table org-staff-uploads-table"
            rowKey="id"
            dataSource={rows}
            columns={queueColumns}
            loading={loading}
            onRow={(row) => ({
              onClick: () => loadBatch(row.id),
              style: { cursor: 'pointer' },
              className: openingBatchId === row.id ? 'staff-upload-row--opening' : undefined,
            })}
            locale={{ emptyText: (
              <Empty
                description={(
                  <Space direction="vertical" size={4}>
                    <Text>{status === 'all' ? 'No staff uploads found' : `No ${formatLabel(status)} staff uploads`}</Text>
                    <Text type="secondary">
                      Older immediate uploads are under Past uploads above.
                    </Text>
                  </Space>
                )}
              />
            ) }}
            scroll={{ x: 900 }}
            pagination={{
              current: meta.page,
              pageSize: meta.limit,
              total: meta.total,
              showSizeChanger: true,
              showTotal: (total) => `${total} batches`,
              onChange: loadBatches,
            }}
          />

          <Drawer
            className="staff-upload-drawer"
            title={batch ? (
              <div className="staff-upload-drawer__title">
                <div className="staff-upload-drawer__title-row">
                  <span className="staff-upload-drawer__heading">
                    {batch.org_name || 'Staff upload'}
                  </span>
                  <Tag color={STATUS_COLORS[batch.status]}>{formatLabel(batch.status)}</Tag>
                </div>
                <div className="staff-upload-drawer__sub">
                  <span>{formatSource(batch)}</span>
                  <span className="staff-upload-drawer__dot">·</span>
                  <span>{batch.filename || `Batch #${batch.id}`}</span>
                  <span className="staff-upload-drawer__dot">·</span>
                  <span>{formatDate(batch.submitted_at)}</span>
                </div>
              </div>
            ) : 'Staff upload'}
            width={Math.min(1080, window.innerWidth)}
            open={Boolean(batch)}
            onClose={() => {
              if (busy) return;
              setBatch(null);
              setSelectedItemIds([]);
            }}
            destroyOnClose
            extra={batch ? (
              <Space size={4} className="staff-upload-drawer__extra">
                <Tooltip title="Download report">
                  <Button
                    type="text"
                    icon={<DownloadOutlined />}
                    loading={downloadingReport}
                    onClick={downloadReport}
                    aria-label="Download report"
                  />
                </Tooltip>
                <Tooltip title="Refresh">
                  <Button
                    type="text"
                    icon={<ReloadOutlined />}
                    loading={detailLoading}
                    onClick={() => loadBatch(batch.id)}
                    aria-label="Refresh"
                  />
                </Tooltip>
              </Space>
            ) : null}
            footer={batch && batchIsActionable && (pendingItems > 0 || hasMarkedDecisions) ? (
              <div className="staff-upload-drawer__footer">
                <Text type="secondary" className="staff-upload-drawer__footer-hint">
                  {selectedDecidableCount > 0
                    ? `${selectedDecidableCount} row${selectedDecidableCount === 1 ? '' : 's'} selected — mark approve or reject, then apply.`
                    : 'Select rows to mark, or mark each row — then apply, or act on the whole batch.'}
                </Text>
                <div className="staff-upload-drawer__footer-actions">
                  {selectedDecidableCount > 0 ? (
                    <>
                      <Tooltip title={`Mark ${selectedDecidableCount} to reject`}>
                        <Button
                          danger
                          icon={<CloseOutlined />}
                          loading={reviewingKey === 'bulk-mark-reject'}
                          disabled={busy && reviewingKey !== 'bulk-mark-reject'}
                          onClick={() => runBulkMark('mark-reject')}
                          aria-label={`Mark ${selectedDecidableCount} to reject`}
                        />
                      </Tooltip>
                      <Tooltip title={`Mark ${selectedDecidableCount} to approve`}>
                        <Button
                          type="primary"
                          icon={<CheckOutlined />}
                          loading={reviewingKey === 'bulk-mark-approve'}
                          disabled={busy && reviewingKey !== 'bulk-mark-approve'}
                          onClick={() => runBulkMark('mark-approve')}
                          aria-label={`Mark ${selectedDecidableCount} to approve`}
                        />
                      </Tooltip>
                      <span className="staff-upload-drawer__footer-divider" aria-hidden />
                    </>
                  ) : null}
                  {hasMarkedDecisions ? (
                    <Tooltip title={enrollmentNeedsResolution ? 'Resolve remaining enrollment conflicts first' : 'Apply decisions'}>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        loading={reviewingKey.startsWith('batch-apply-decisions')}
                        disabled={enrollmentNeedsResolution || (busy && !reviewingKey.startsWith('batch-apply-decisions'))}
                        onClick={() => runDecision('batch', 'apply-decisions')}
                        aria-label="Apply decisions"
                      />
                    </Tooltip>
                  ) : null}
                  {pendingItems > 0 ? (
                    <>
                      <Tooltip title="Reject all">
                        <Button
                          danger
                          icon={<CloseOutlined />}
                          loading={reviewingKey.startsWith('batch-reject')}
                          disabled={busy && !reviewingKey.startsWith('batch-reject')}
                          onClick={() => runDecision('batch', 'reject')}
                          aria-label="Reject all"
                        />
                      </Tooltip>
                      <Tooltip title={enrollmentNeedsResolution ? 'Remaining conflicts are in the enrollment holds inbox. Already-onboarded rows are on the onboarding list and are not paused.' : 'Approve all'}>
                        <Button
                          type="primary"
                          icon={<CheckOutlined />}
                          loading={reviewingKey.startsWith('batch-approve')}
                          disabled={enrollmentNeedsResolution || (busy && !reviewingKey.startsWith('batch-approve'))}
                          onClick={() => runDecision('batch', 'approve')}
                          aria-label="Approve all"
                        />
                      </Tooltip>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}
          >
            {batch ? (
              <div className="staff-upload-drawer__body">
                {enrollmentNeedsResolution ? (
                  <EnrollmentPausedBanner
                    style={{ marginBottom: 12 }}
                    data={{
                      session_id: enrollmentSessionId,
                      needs_resolution: true,
                      status: (Number(enrollmentSummary.enrollment_applied_item_count) > 0
                        || Number(enrollmentSummary.enrollment_onboarded_count) > 0
                        || Number(enrollmentSummary.enrollment_updated_count) > 0
                        || Number(enrollmentSummary.enrollment_dismissed_same_count) > 0)
                        ? 'partially_applied'
                        : 'needs_resolution',
                      onboarded_count: enrollmentSummary.enrollment_onboarded_count,
                      enrollment_onboarded_count: enrollmentSummary.enrollment_onboarded_count,
                      updated_count: enrollmentSummary.enrollment_updated_count,
                      enrollment_updated_count: enrollmentSummary.enrollment_updated_count,
                      dismissed_as_same_count: enrollmentSummary.enrollment_dismissed_same_count,
                      enrollment_dismissed_same_count: enrollmentSummary.enrollment_dismissed_same_count,
                      needs_resolution_count: enrollmentSummary.enrollment_conflict_count,
                      open_conflict_count: enrollmentSummary.enrollment_conflict_count,
                      conflict_count: enrollmentSummary.enrollment_conflict_count,
                      enrollment_conflict_count: enrollmentSummary.enrollment_conflict_count,
                      held_clean_count: enrollmentSummary.enrollment_held_clean_count,
                      clean_count: enrollmentSummary.enrollment_held_clean_count,
                      social_no: filterSocialNo,
                    }}
                    socialNo={resolvePlainOrgCode(filterSocialNo)}
                    onDownload={enrollmentSessionId ? async () => {
                      try {
                        await lib.exportEnrollmentConflictsCsv({
                          session_id: enrollmentSessionId,
                          status: 'open',
                        });
                      } catch (err) {
                        notification.error({ message: err?.message || 'Export failed' });
                      }
                    } : undefined}
                  />
                ) : null}
                {Number(batch?.failed_count) > 0 || String(batch?.status || '').toLowerCase() === 'failed' ? (
                  <Alert
                    type="error"
                    showIcon
                    className="staff-upload-drawer__alert"
                    message={Number(batch?.failed_count) > 0
                      ? `${Number(batch.failed_count)} row(s) could not be applied`
                      : 'This upload could not be applied'}
                    description={(
                      <Space direction="vertical" size={4}>
                        <span>
                          The apply was rolled back — no partial changes were saved. Check the
                          {' '}<strong>Result</strong> column on the failed rows below for the reason,
                          fix the data, then retry.
                        </span>
                        <Button
                          type="link"
                          size="small"
                          icon={<DownloadOutlined />}
                          loading={downloadingReport}
                          onClick={downloadReport}
                          style={{ padding: 0, height: 'auto' }}
                        >
                          Download report for full per-row reasons
                        </Button>
                      </Space>
                    )}
                  />
                ) : !batchIsActionable ? (
                  <Alert
                    type="info"
                    showIcon
                    className="staff-upload-drawer__alert"
                    message="This batch is closed"
                    description="No further decisions are needed."
                  />
                ) : null}

                {batch.clear_pending_activations ? (
                  <Alert
                    type="warning"
                    showIcon
                    className="staff-upload-drawer__alert"
                    message="Clear pending activations"
                    description="Approving this batch will also remove unfinished activations for this org (not active policies)."
                  />
                ) : null}

                <section className="staff-upload-drawer__section staff-upload-drawer__summary">
                  <div className="staff-upload-metric-grid staff-upload-metric-grid--compact">
                    {decisionMetrics.map((metric) => (
                      <div key={metric.key} className={`staff-upload-metric staff-upload-metric--${metric.tone}`}>
                        <span className="staff-upload-metric__label">{metric.label}</span>
                        <span className="staff-upload-metric__value">{metric.value}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="staff-upload-drawer__section staff-upload-drawer__section--items">
                  <div className="staff-upload-drawer__section-head staff-upload-drawer__section-head--row">
                    <div>
                      <h4>Rows</h4>
                      <Text type="secondary">
                        {filteredItems.length} shown
                        {filteredItems.length !== batchItems.length ? ` of ${batchItems.length}` : ''}
                      </Text>
                    </div>
                    <Space wrap size="small">
                      {selectedDecidableCount > 0 ? (
                        <>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {selectedDecidableCount} selected
                          </Text>
                          <Tooltip title={`Mark ${selectedDecidableCount} to reject`}>
                            <Button
                              size="small"
                              danger
                              icon={<CloseOutlined />}
                              loading={reviewingKey === 'bulk-mark-reject'}
                              disabled={busy && reviewingKey !== 'bulk-mark-reject'}
                              onClick={() => runBulkMark('mark-reject')}
                              aria-label={`Mark ${selectedDecidableCount} to reject`}
                            />
                          </Tooltip>
                          <Tooltip title={`Mark ${selectedDecidableCount} to approve`}>
                            <Button
                              size="small"
                              type="primary"
                              icon={<CheckOutlined />}
                              loading={reviewingKey === 'bulk-mark-approve'}
                              disabled={busy && reviewingKey !== 'bulk-mark-approve'}
                              onClick={() => runBulkMark('mark-approve')}
                              aria-label={`Mark ${selectedDecidableCount} to approve`}
                            />
                          </Tooltip>
                        </>
                      ) : null}
                      <Select
                        value={actionType}
                        onChange={setActionType}
                        style={{ minWidth: 150 }}
                        options={[
                          { value: 'all', label: 'All intents' },
                          ...['create_new', 'update_existing', 'reactivate', 'deactivate', 'activate']
                            .map((value) => ({ value, label: formatLabel(value) })),
                        ]}
                      />
                      <Select
                        value={itemStatus}
                        onChange={setItemStatus}
                        style={{ minWidth: 150 }}
                        options={[
                          { value: 'all', label: 'All statuses' },
                          { value: 'pending', label: 'Pending' },
                          { value: 'marked_approve', label: 'Approve' },
                          { value: 'marked_reject', label: 'Reject' },
                          { value: 'applied', label: 'Approved' },
                          { value: 'rejected', label: 'Rejected' },
                          { value: 'failed', label: 'Failed' },
                        ]}
                      />
                    </Space>
                  </div>

                  <Table
                    className="staff-upload-drawer__table"
                    size="middle"
                    rowKey="id"
                    dataSource={filteredItems}
                    columns={itemColumns}
                    loading={detailLoading}
                    rowSelection={itemRowSelection}
                    rowClassName={itemRowClassName}
                    scroll={{ x: 960 }}
                    pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} items` }}
                  />
                </section>
              </div>
            ) : null}
          </Drawer>
        </>
      )}
    </>
  );
};

export default StaffUploadsPanel;
